import { apiClient } from '@/lib/api-client';

/**
 * WhatsApp voice calling. Server contract:
 * `apps/api/src/modules/doubletick/whatsapp-call.controller.ts`
 * (docs/integrations/doubletick-whatsapp-calling.md).
 *
 * Call state is provider-owned: it is built from CALL_* webhooks and only
 * read here. Nothing in this module invents or mutates a status.
 */

export type WhatsAppCallDirection = 'INCOMING' | 'OUTGOING';
export type WhatsAppCallStatus =
  | 'INITIATED'
  | 'IN_PROGRESS'
  | 'END'
  | 'REJECT'
  | 'MISSED'
  | 'FAILED';

export interface WhatsAppCall {
  id: string;
  callId: string;
  conversationId: string | null;
  phone: string;
  contactName: string | null;
  customerId: string | null;
  leadId: string | null;
  direction: WhatsAppCallDirection;
  status: WhatsAppCallStatus;
  failedReason: string | null;
  durationSeconds: number | null;
  pickedBy: string | null;
  handledById: string | null;
  recordingUrl: string | null;
  /** S3 key of our own browser recording; plays through the same proxy. */
  recordingKey: string | null;
  /** Customer's SDP offer on an inbound call - what the softphone answers. */
  offerSdp: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
}

export interface WhatsAppCallPermission {
  /** True when a business-initiated call can be placed right now. */
  allowed: boolean;
  /** Why not, when `allowed` is false. */
  reason: string | null;
  expiresAt: string | null;
  usage: { current: number; max: number } | null;
  /** Remaining permission-request sends to this customer (Meta caps them hard). */
  requestQuota: {
    allowed: boolean;
    leftToday: number | null;
    leftThisWeek: number | null;
    limit: number | null;
    resetAt: string | null;
  } | null;
  raw: unknown;
}

export interface WhatsAppCallSettings {
  integrationId: string | null;
  wabaNumber: string | null;
  voiceCallEnabled: boolean;
  allowPeopleRequestCallback: boolean;
  displayCallButton: boolean;
  callHours: unknown;
}

export const fetchCallsForLead = async (leadId: string): Promise<WhatsAppCall[]> => {
  const { data } = await apiClient.get<WhatsAppCall[]>(`/whatsapp/calls/by-lead/${leadId}`);
  return data;
};

export const fetchCallsForConversation = async (
  conversationId: string,
): Promise<WhatsAppCall[]> => {
  const { data } = await apiClient.get<WhatsAppCall[]>(
    `/whatsapp/calls/by-conversation/${conversationId}`,
  );
  return data;
};

/**
 * Recordings (our own S3 upload, or DoubleTick's, which 403s without the
 * account API key) are streamed through the API and handed to <audio> as an
 * object URL.
 */
export const fetchCallRecordingObjectUrl = async (callId: string): Promise<string> => {
  const { data } = await apiClient.get<Blob>(
    `/whatsapp/calls/${encodeURIComponent(callId)}/recording`,
    { responseType: 'blob' },
  );
  return URL.createObjectURL(data);
};

/** Whether the customer has consented to being called. */
export const fetchCallPermission = async (phone: string): Promise<WhatsAppCallPermission> => {
  const { data } = await apiClient.get<WhatsAppCallPermission>('/whatsapp/calls/permission', {
    params: { phone },
  });
  return data;
};

export const fetchCallSettings = async (): Promise<WhatsAppCallSettings[]> => {
  const { data } = await apiClient.get<WhatsAppCallSettings[]>('/whatsapp/calls/settings');
  return data;
};

export const updateCallSettings = async (
  input: Partial<
    Pick<
      WhatsAppCallSettings,
      'voiceCallEnabled' | 'allowPeopleRequestCallback' | 'displayCallButton'
    >
  >,
): Promise<WhatsAppCallSettings[]> => {
  const { data } = await apiClient.post<WhatsAppCallSettings[]>('/whatsapp/calls/settings', input);
  return data;
};

/**
 * Place a business-initiated call. The SDP offer comes from the softphone's
 * RTCPeerConnection; the provider's SDP answer comes back in the response.
 */
export const createCall = async (input: {
  phone: string;
  sdp: string;
  deviceId?: string;
}): Promise<{ callId: string; answer: string }> => {
  const { data } = await apiClient.post<{ callId: string; answer: string }>(
    '/whatsapp/calls/create',
    input,
  );
  return data;
};

/** One call as the webhooks recorded it; null until the first webhook lands. */
export const fetchCallStatus = async (callId: string): Promise<WhatsAppCall | null> => {
  const { data } = await apiClient.get<WhatsAppCall | null>(
    `/whatsapp/calls/${encodeURIComponent(callId)}/status`,
  );
  return data ?? null;
};

/** Keepalive - the provider drops a call that stops pinging. */
export const pingCall = async (callId: string): Promise<void> => {
  await apiClient.post(`/whatsapp/calls/${encodeURIComponent(callId)}/ping`);
};

/** Hang up a live call, or reject one that is still ringing. */
export const declineCall = async (callId: string): Promise<void> => {
  await apiClient.post(`/whatsapp/calls/${encodeURIComponent(callId)}/decline`);
};

/** Answer a ringing inbound call with the softphone's SDP answer. */
export const acceptCall = async (input: {
  callId: string;
  sdp: string;
  deviceId?: string;
}): Promise<void> => {
  await apiClient.post('/whatsapp/calls/accept', input);
};

/**
 * Upload a browser recording: presign, PUT straight to S3, then finalize so
 * the API links it to the call.
 */
export const uploadCallRecording = async (input: {
  callId: string;
  phone: string;
  direction: WhatsAppCallDirection;
  blob: Blob;
}): Promise<void> => {
  const id = encodeURIComponent(input.callId);
  const { data } = await apiClient.post<{ url: string; key: string }>(
    `/whatsapp/calls/${id}/recording/upload-init`,
  );
  const put = await fetch(data.url, {
    method: 'PUT',
    headers: { 'Content-Type': 'audio/webm' },
    body: input.blob,
  });
  if (!put.ok) throw new Error(`Recording upload failed (${put.status}).`);
  await apiClient.post(`/whatsapp/calls/${id}/recording/finalize`, {
    phone: input.phone,
    direction: input.direction,
  });
};

/** One line per DoubleTick calling license - dialling is blocked while all are busy. */
export interface WhatsAppCallLines {
  total: number;
  busy: number;
  free: number;
}

export const fetchCallLines = async (): Promise<WhatsAppCallLines> => {
  const { data } = await apiClient.get<WhatsAppCallLines>('/whatsapp/calls/lines');
  return data;
};

/** Give the calling line back when a call ended without a hang-up request. */
export const releaseCallLine = async (callId: string): Promise<void> => {
  await apiClient.post(`/whatsapp/calls/${encodeURIComponent(callId)}/release-line`);
};
